import {
  WaiversStep,
  type SignedSet,
  type WaiverFieldErrors,
  type WaiverPersonalInfo,
} from './WaiversStep';
import { IdentityStep, type IdentityStatus } from './IdentityStep';
import { FieldError } from './FieldError';
import type { WaiverKind } from '@/lib/api';

export interface WaiverAndIdentityFieldErrors extends WaiverFieldErrors {
  identity?: string | null;
}

/**
 * Combined step: sign required waivers, then verify government ID.
 * The ID verification UI only appears once every required waiver is signed,
 * so the user always completes legal sign-off before the ID handoff.
 */
export function WaiverAndIdentityStep(props: {
  required: WaiverKind[];
  authToken: string;
  legalName: string;
  setLegalName: (s: string) => void;
  signed: SignedSet;
  setSigned: (s: SignedSet) => void;
  signerEmail: string;
  personalInfo: WaiverPersonalInfo;
  setPersonalInfo: (info: WaiverPersonalInfo) => void;
  identityStatus: IdentityStatus;
  setIdentityStatus: (s: IdentityStatus) => void;
  fieldErrors?: WaiverAndIdentityFieldErrors;
  clearFieldError?: (key: keyof WaiverAndIdentityFieldErrors) => void;
}) {
  const allSigned = props.required.every((k) => Boolean(props.signed[k]));
  const fieldErrors = props.fieldErrors ?? {};
  const setIdentity = (s: IdentityStatus) => {
    props.setIdentityStatus(s);
    if (s === 'verified') props.clearFieldError?.('identity');
  };
  return (
    <div className="grid gap-6">
      <WaiversStep
        required={props.required}
        authToken={props.authToken}
        legalName={props.legalName}
        setLegalName={props.setLegalName}
        signed={props.signed}
        setSigned={props.setSigned}
        signerEmail={props.signerEmail}
        personalInfo={props.personalInfo}
        setPersonalInfo={props.setPersonalInfo}
        fieldErrors={fieldErrors}
        clearFieldError={props.clearFieldError}
      />
      {allSigned && (
        <div
          id="field-identity"
          tabIndex={-1}
          className="border-t border-bone/10 pt-6"
          data-testid="identity-block"
        >
          <h4 className="mb-3 font-display text-lg">Verify your government ID</h4>
          <IdentityStep
            authToken={props.authToken}
            status={props.identityStatus}
            setStatus={setIdentity}
          />
          <FieldError message={fieldErrors.identity} />
        </div>
      )}
    </div>
  );
}
